import {
  Box,
  Typography,
  Divider as MuiDivider,
  withStyles,
  Theme,
  Link,
  Backdrop,
  makeStyles,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import styled from "styled-components/macro";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
} from "@react-pdf/renderer";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useEffectAsync } from "../../../hooks/useEffectAsync";
import { FarData } from "../../../models/Far";
import { Property } from "../../../models/Property";
import { fetchCompany } from "../../../redux/actions/companyActions";
import { useDispatch, useSelector } from "../../../redux/store";
import { fetchFAR } from "../../../services/full-accessibility-report-service";
import { CompactTable } from "./Table/CompactTable";
import { useTranslation } from "react-i18next";
import {getCurrectLanguageFullName, getTextByCurrentLanguage, replaceText} from "../../../utils/functions";
import { NavLink } from "react-router-dom";
import InfoCard from "../../../components/InfoCard";
import PropertiesCardPagination from "../../../components/PropertiesCardPagination";
import { Alert } from "@material-ui/lab";

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    backgroundColor: "#FAFAFA",
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  coverWrapper: {
    width: "100%",
    height: "100%",
    padding: 30,
  },
  coverTitle: {
    fontSize: 54,
    fontFamily: "Manrope",
    color: "#00FFA2",
    fontWeight: "bold",
    width: "70%",
  },
  coverSubtitle: {
    marginTop: 36,
    fontSize: 18,
    fontFamily: "Manrope",
    color: "#00FFA2",
    width: "70%",
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  bulletPoint: {
    width: 10,
    fontSize: 10,
  },
  item: {
    flexDirection: "row",
    marginBottom: 5,
  },
  itemContent: {
    flex: 1,
    fontSize: 10,
  },
});

const useStyles = makeStyles({
  infoAlert: {
    "& .MuiAlert-icon": {
      color: "#B8E7FB",
    },
    marginTop: "30px",
    backgroundColor: "#233145",
  },
});

const PDFWrapper = withStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: "800px",
    [theme.breakpoints.up("xl")]: {
      height: "1186px",
    },
    [theme.breakpoints.down("lg")]: {
      height: "900px",
    },
    [theme.breakpoints.down("md")]: {
      height: "700px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "600px",
    },
  },
}))(Box);

const Divider = styled(MuiDivider)(spacing);

export default function FAR() {
  const company = useSelector((state) => state.companyReducer.company);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.authReducer.user);
  const userProfile: any = useSelector((state) => state.userReducer.user);
  const [property, setProperty] = useState<Property>(null!);
  const [farData, setFarData] = useState<FarData[]>([]);
  const [noFarData, setNoFarData] = useState(false);
  const [loading, setLoading] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    if (userProfile && userProfile.company) {
      dispatch(fetchCompany(userProfile.company, user!.token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  useEffectAsync(async () => {
    if (!property) return;
    const farData = await fetchFAR(
      {
        limit: 999,
        property__id: property.id,
      },
      user!.token
    );

    let foundFarAccData = false;
    farData.results.forEach((data) => {
      if (data.accessibilities.length > 0) {
        foundFarAccData = true;
      }
    });
    farData.results = farData.results.map((data) => {
      if (data.accessibilities.length > 0) {
        data.accessibilities = data.accessibilities.map((acc: any) => ({
          ...acc,
          attribute_title: replaceText(acc.attribute_title, acc?.item_title, getTextByCurrentLanguage(data?.name)),
          question: replaceText(acc.question, acc?.item_title, getTextByCurrentLanguage(data?.name))
        }))
      }

      return data
    })

    if (farData.results.length === 0 || !foundFarAccData) {
      setNoFarData(true);
    } else if (farData.results.length > 0 && foundFarAccData) {
      setLoading(true);
      setNoFarData(false);
    }

    setFarData(farData.results);
  }, [property]);

  const pdfRendered = () => setLoading(false);

  const DOC = useMemo(() => {
    if (!property) {
      return () => (
        <Document>
          <Page size="A4">
            <View style={styles.coverWrapper}>
              <Text
                style={{
                  width: "100%",
                  fontSize: 30,
                  color: "#00FFA2",
                  fontFamily: "Manrope",
                }}
              >
                {t("FAR_SelectProperty")}
              </Text>
            </View>
            <Image
              style={{
                width: "100%",
                height: "100%",
                // objectFit: "contain",
                position: "absolute",
                zIndex: -1,
              }}
              src={`${process.env.PUBLIC_URL}/static/img/covers/bg_report.png`}
            />
          </Page>
        </Document>
      );
    }
    return () => (
      <Document onRender={pdfRendered}>
        <Page size="A4">
          <View style={styles.coverWrapper}>
            <Image
              style={{
                width: "50px",
                height: "50px",
                marginTop: "80px",
              }}
              src={`${process.env.PUBLIC_URL}/static/img/icons/accessibility-report-cover-icon-2.0.png`}
            />
            <Text style={styles.coverTitle}>{t("FAR_Report")}</Text>
            <Text style={styles.coverSubtitle}>{getTextByCurrentLanguage(property.name)}</Text>
            <View
              style={{
                position: "absolute",
                bottom: styles.coverWrapper.padding,
                left: styles.coverWrapper.padding,
                width: "100%",
              }}
            >
              <View
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                }}
              >
                <Image
                  style={{
                    width: "auto",
                    height: "45px",
                    objectFit: "contain",
                  }}
                  src={`${process.env.PUBLIC_URL}/static/img/icons/HD-accessibility-logo-light-2.0.png`}
                />
              </View>
              <View
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  fontSize: 12,
                  color: "white",
                  fontFamily: "Manrope",
                }}
              >
                <Text>{getCurrectLanguageFullName()}</Text>
                <Text>
                  {t("CLP_GeneratedOn")}{" "}
                  {new Date()
                    .toLocaleDateString(i18n.language, {
                      year: "numeric",
                      month: "long",
                      day: "2-digit",
                    })
                    .toLocaleUpperCase()}
                </Text>
                <Text>
                  {t("CLP_GeneratedBy")} {userProfile.first_name}{" "}
                  {userProfile.last_name}
                </Text>
              </View>
            </View>
          </View>
          <Image
            style={{
              width: "100%",
              height: "100%",
              // objectFit: "contain",
              position: "absolute",
              zIndex: -1,
            }}
            src={`${process.env.PUBLIC_URL}/static/img/covers/bg_report.png`}
          />
        </Page>
        <Page size="A4" style={styles.page} wrap>
          <View
            style={{
              width: "100%",
            }}
          >
            <View key={property.id}>
              <Text
                style={{
                  fontSize: 25,
                  marginBottom: 30,
                }}
              >
                {getTextByCurrentLanguage(property.name)}
              </Text>
              <CompactTable data={farData} />
            </View>
          </View>
          <View
            fixed
            style={{
              position: "absolute",
              bottom: 15,
              width: "100%",
            }}
          >
            <View
              style={{
                position: "absolute",
                bottom: 0,
                left: styles.page.paddingHorizontal,
              }}
            >
              <Image
                style={{
                  left: 0,
                  width: "auto",
                  height: "25px",
                  objectFit: "contain",
                }}
                src={`${process.env.PUBLIC_URL}/static/img/icons/HD-accessibility-logo-footer-2.0.png`}
              />
            </View>
            <View
              style={{
                position: "absolute",
                bottom: 0,
                right: -styles.page.paddingHorizontal,
              }}
            >
              <Text
                style={{
                  fontSize: 12,
                  color: "#1B2635",
                }}
                render={({ pageNumber }) => `${pageNumber}`}
              />
            </View>
          </View>
        </Page>
      </Document>
    );
  }, [farData]);

  if (!company) {
    return <div></div>;
  }

  const onActiveProperty = (property: Property) => setProperty(property);

  return (
    <Box p={12} className={`disable-scrolls`} overflow="scroll">
      <Helmet title={t("routes_Far")} />
      <Typography variant="h3" gutterBottom display="inline">
        {t("routes_Far")}
      </Typography>
      <Divider my={6} />

      <InfoCard title={t("FAR_WhatIsItTitle")}>
        <Typography
          variant="body2"
          gutterBottom
          style={{ color: "rgba(238, 238, 238, 0.5)" }}
        >
          {t("FAR_WhatIsItDescription")}
        </Typography>
        <Typography
          variant="body2"
          gutterBottom
          style={{ color: "rgba(238, 238, 238, 0.5)" }}
        >
          {t("FAR_MoreInfo")}{" "}
          <Link component={NavLink} exact to="/documentation">
            {t("CLP_FAR_ConsultTheDocumentation")}.
          </Link>
        </Typography>
      </InfoCard>

      {user && user.token ? (
        <PropertiesCardPagination
          token={user.token}
          onActiveProperty={onActiveProperty}
        />
      ) : null}

      <Backdrop style={{ color: "#fff", zIndex: 99999999 }} open={loading}>
        <Typography
          style={{ fontSize: "16px" }}
          variant="caption"
          display="block"
        >
          {t("CLP_LoadingDoc")}
        </Typography>
      </Backdrop>

      {noFarData && property ? (
        <Alert className={classes.infoAlert} severity="info">
          {t("FAR_noDataAvalible_1")}{" "}
          <Link
            component={NavLink}
            exact
            to={`/standards/${property.id}/classify`}
          >
            {t("facilitiesForm_standard")}
          {" "}
          {t("FAR_noDataAvalible_2")} {getTextByCurrentLanguage(property.name)}
          </Link>
          {" "}
          {t("FAR_noDataAvalible_3")}
        </Alert>
      ) : !property ?
        <Alert className={classes.infoAlert} severity="info">
          {t("CLP_FAR_noAccessibilityDataAvailable1")}
          <Link component={NavLink} exact to="/standards">
            {t("CLP_FAR_noAccessibilityDataAvailable2")}
          </Link>
          {t("CLP_FAR_noAccessibilityDataAvailable3")}
        </Alert> : null
      }
      {farData && farData.length > 0 && !noFarData ? (
        <>
          <Box marginBottom={"30px"}></Box>
          <Typography variant="h5">{t("FAR_DocumentPreview")} </Typography>
          <PDFWrapper>
            <PDFViewer style={{ width: "100%", height: "100%" }}>
              <DOC />
            </PDFViewer>
          </PDFWrapper>
        </>
      ) : null}
    </Box>
  );
}
